<template>
  <a-modal :title="title" :open="open" centered :maskClosable="false" @cancel="handleClose">
    <section class="wrapper">
      <a-form layout="vertical" :model="formState">

        <a-form-item label="Tipo de respuesta rápida" required>
          <a-radio-group v-model:value="formState.reply_type" :disabled="isEdit">
            <a-radio :value=1>Normal</a-radio>
            <a-radio :value=2>Automatizada</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <p v-if="formState.reply_type === 1">
          Para usar una respuesta rápida normal, tus agentes deben presionar el ícono <ThunderboltOutlined /> desde su módulo de Workspace.
        </p>
        <p v-else>
          Las respuestas rápidas automatizadas se pueden usar solo al tipificar. Recuerda habilitar su envío en la configuración de cada cola.
        </p>

        <a-form-item label="Título" required>
          <a-input 
            v-model:value="formState.title" 
            placeholder="Escribir titulo" 
            @input="showConfirmModal = true"
          />
        </a-form-item>

        <a-form-item label="Mensaje de respuesta rápida" required>
          <!-- <a-textarea
            v-model:value="formState.message"
            placeholder="Escribir mensaje..."
            show-count
            :maxlength="980"
            :rows="2"
            @input="showConfirmModal = true"
          /> -->
          <RichLinkTextarea
            v-model="formState.message"
            placeholder="Escribir mensaje..."
            :showCount="true"
            :maxlength="980"
            :rows="3"
            :disabled="false"
            @input="showConfirmModal = true"
          />
          <InputActionButtons
            v-model:file="formState.file"
            @select-emoji="formState.message += $event"
          />
        </a-form-item>
     
        <CampaignPicker
          v-model:value="formState.portfolios"
          withName
          formName="portfolios"
          withOutEntity
          multiple
          :showAll="false"
          label="Campañas"
          :rules="[{ required: true, message: 'Por favor ingresar campaña' }]"
          @update:value="showConfirmModal = true"
        />
        
        <QueuePicker
          v-model:value="formState.queues"
          name="queues"
          label="Colas asignadas"
          help="Esta respuesta rápida aparecerá en los tickets asociados a las colas seleccionadas."
          :showAll="false"
          :withOutPortfolio="false"
          :nullwithOutPortfolio="true"
          multiple
          :portfolio="formState.portfolios"
          :rules="[{ required: true, message: '' }]"
          @update:value="showConfirmModal = true"
        />
       
      </a-form>
    </section>

    <template #footer>
      <footer>
        <a-button @click="handleClose">Cancelar</a-button>

        <a-tooltip style="width: 100%">
          <template #title v-if="isDisabled">
            {{ disableReason }}
          </template>
          <a-button type="primary" :loading="loading" :disabled="isDisabled" @click="handlePrimary">
            {{ type === "create" ? "Crear respuesta rápida" : "Guardar cambios" }}
          </a-button>
        </a-tooltip>
      </footer>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, reactive, readonly, ref, createVNode } from "vue";
import { message, Modal } from "ant-design-vue";
import { ThunderboltOutlined } from "@ant-design/icons-vue";

import {
  QuickAnswer,
  ModalActionType,
  QuickAnswerCreateBody,
} from "@/@types/platformSettings/quickAnswers";
import { quickAnswersService } from "../../services/QuickActionsService";
import { ResponseError } from "@/app/shared/utils/httpManager";

import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import InputActionButtons from "@/app/shared/components/buttons/InputActionButtons.vue";
import { cloneDeep, isEqual } from "lodash";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";
import RichLinkTextarea from "@/app/platformSettings/components/input/RichLinkTextarea.vue";

const props = defineProps<{
  open: boolean;
  type: ModalActionType;
  record: QuickAnswer | undefined;
}>();

const emit = defineEmits<{ "update:open": [open: boolean]; update: [] }>();

const title = computed(() => {
  let prefix = "";

  if (props.type === "create") prefix = "Crear";
  if (props.type === "edit") prefix = "Editar";

  return `${prefix} respuesta rápida`;
});

const formState = reactive<QuickAnswerCreateBody>({
  reply_type: props.record?.reply_type || 1,
  title: props.record?.title || "",
  file: props.record?.file_url || undefined,
  message: props.record?.message || "",
  portfolios: props.record?.portfolios?.length ? props.record?.portfolios.map((p) => p.id) : [],
  queues: props.record?.queues?.length ? props.record?.queues.map((q) => q.id) : [],
});

const baseForm = readonly(cloneDeep(formState));

const hasReplyType = computed(() => formState.reply_type === 1 || formState.reply_type === 2);
const hasTitle = computed(() => formState.title?.trim().length > 0);
const hasMessage = computed(() => formState.message?.trim().length > 0);
const hasPortfolios = computed(() => Array.isArray(formState.portfolios) && formState.portfolios.length > 0);
const hasQueues = computed(() => Array.isArray(formState.queues) && formState.queues.length > 0);

const requiredFilled = computed(
  () => hasReplyType.value && hasTitle.value && hasMessage.value && hasPortfolios.value && hasQueues.value
);

const hasChanges = computed(() => !isEqual(baseForm, formState));

const isDisabled = computed(() => {
  if (!requiredFilled.value) return true;
  if (props.type === "edit" && !hasChanges.value) return true;
  return false;
});

const disableReason = computed(() => {
  if (!requiredFilled.value) return "Completa los campos requeridos para crear tu respuesta rápida.";
  if (props.type === "edit" && !hasChanges.value) return "No hay nuevos cambios por guardar.";
  return "";
});

const isEdit = computed(() => props.type === "edit");

const showConfirmModal = ref(false);

const handleClose = () => {
  if (showConfirmModal.value) {
    Modal.confirm({
      title: 
        props.type === "create"
        ? "¿Deseas descartar tu progreso realizado?"
        : "¿Deseas descartar los cambios realizados?",
      content: 
        props.type === "create"
        ? "Al descartar, no se quedarán guardados los datos previamente completados."
        : "Recuerda que, si no guardas los cambios que realizaste, estos serán descartados.",
      icon: createVNode(QuestionCircleOutlined),
      onOk: () => handleCancel(),
      centered: true,
      okText: "Si, descartar",
      cancelText: props.type === "create" ? "No, seguir completando" : "No, seguir editando",
    })
  } else {
    handleCancel();
  }
};

const handleCancel = () => {
  showConfirmModal.value = false;
  emit("update:open", false);
};

const loading = ref(false);

const handlePrimary = async () => {
  loading.value = true;

  try {
    if (props.type === "create") await quickAnswersService.create(formState);
    if (props.type === "edit") await quickAnswersService.edit(props.record?.id, formState);

    emit("update");
    emit("update:open", false);

    if (props.type === "create") message.success("Respuesta rápida creada con éxito.");
    if (props.type === "edit") message.success("Cambios guardados con éxito.");

  } catch (error) {
    console.error(error);
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
  }

  loading.value = false;
};
</script>

<style scoped lang="sass">
.ant-form-item
  margin-bottom: 20px

:deep(.ant-input-textarea-show-count::after)
  position: absolute !important
  bottom: 2px
  right: 8px
  font-size: 12px
  color: $gray-6

footer
  width: 100%
  display: flex
  justify-content: flex-end
  gap: 8px

.wrapper
  padding: 20px

p
  color: $text-secondary
  margin-bottom: 20px
</style>
